import { useEffect, useRef, useState } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  ListItem,
  SegmentedControl,
} from '@/components/ui';
import { ROUTES } from '@/config/routes';
import { usePermissions } from '@/contexts/PermissionsContext';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import { PermissionKey, PERMISSIONS } from '@/types/permissions';
import {
  IconCheck,
  IconDatabaseImport,
  IconFiles,
  IconHistory,
  IconMenu,
  IconShield,
  IconTable,
  IconUpload,
} from '@tabler/icons-react';

import { Link, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';

interface NavItem {
  href: string;
  label: string;
  permissions: PermissionKey[];
  icon?: React.ComponentType<{ size?: number; className?: string }>;
}

// Navigation layout constants
const LOGO_WIDTH = 170; // Logo width in pixels
const RIGHT_SECTION_WIDTH = 550; // Fixed width for right section elements
const NAVBAR_PADDING = 16; // Total padding (8px on each side)
const NAV_ITEM_WIDTH = 140; // Approximate width of one navigation item (rounded from 132px)

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMounted, setIsMounted] = useState(false);
  const [itemsThatFit, setItemsThatFit] = useState(0);
  const navRef = useRef<HTMLDivElement>(null);
  const { isLoading } = usePermissions();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Calculate available space and items that fit
  const calculateItemsThatFit = (screenWidth: number): number => {
    // Calculate available space for navigation
    // Available = screenWidth - rightSection (fixed) - padding - logo
    const availableSpace =
      screenWidth - RIGHT_SECTION_WIDTH - NAVBAR_PADDING - LOGO_WIDTH;

    // Calculate how many items can fit (floor to ensure we don't overflow)
    const itemsFit = Math.floor(availableSpace / NAV_ITEM_WIDTH);

    return Math.max(0, itemsFit); // Ensure non-negative
  };

  // Check available space and update items that fit
  useEffect(() => {
    const updateItemsThatFit = () => {
      const screenWidth = window.innerWidth;
      const fit = calculateItemsThatFit(screenWidth);
      setItemsThatFit(fit);
    };

    updateItemsThatFit();
    window.addEventListener('resize', updateItemsThatFit);

    return () => window.removeEventListener('resize', updateItemsThatFit);
  }, []);

  const navItems: NavItem[] = [
    {
      href: ROUTES.studyFiles,
      label: 'Study Files',
      permissions: [PERMISSIONS.STUDY_FILES_ACCESS],
      icon: IconFiles,
    },
    {
      href: ROUTES.fileImporter,
      label: 'File Upload',
      permissions: [PERMISSIONS.FILES_ACCESS],
      icon: IconUpload,
    },
    {
      href: ROUTES.csvImporter,
      label: 'CSV Upload',
      permissions: [PERMISSIONS.CSV_ACCESS],
      icon: IconTable,
    },
    {
      href: ROUTES.studyDataImporter,
      label: 'Data Import',
      permissions: [PERMISSIONS.DATA_ACCESS],
      icon: IconDatabaseImport,
    },
    {
      href: ROUTES.whitelistManagement,
      label: 'Whitelist',
      permissions: [PERMISSIONS.WHITELIST_ACCESS],
      icon: IconShield,
    },
    {
      href: ROUTES.auditTrail,
      label: 'Audit Trail',
      permissions: [PERMISSIONS.AUDIT_TRAIL_ACCESS],
      icon: IconHistory,
    },
  ];

  // Check permissions for each nav item
  const studyFilesAccess = usePermissionAccess([
    PERMISSIONS.STUDY_FILES_ACCESS,
  ]);
  const fileImporterAccess = usePermissionAccess([PERMISSIONS.FILES_ACCESS]);
  const csvImporterAccess = usePermissionAccess([PERMISSIONS.CSV_ACCESS]);
  const studyDataImporterAccess = usePermissionAccess([
    PERMISSIONS.DATA_ACCESS,
  ]);
  const whitelistManagementAccess = usePermissionAccess([
    PERMISSIONS.WHITELIST_ACCESS,
  ]);
  const auditTrailAccess = usePermissionAccess([
    PERMISSIONS.AUDIT_TRAIL_ACCESS,
  ]);

  // Filter items based on permissions
  const availableItems = navItems.filter((item, index) => {
    switch (index) {
      case 0:
        return studyFilesAccess;
      case 1:
        return fileImporterAccess;
      case 2:
        return csvImporterAccess;
      case 3:
        return studyDataImporterAccess;
      case 4:
        return whitelistManagementAccess;
      case 5:
        return auditTrailAccess;
      default:
        return false;
    }
  });

  const segmentedItems = availableItems.map(item => ({
    value: item.href,
    label: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {item.icon && <item.icon />}
        <span>{item.label}</span>
      </div>
    ),
  }));

  // Helper function to determine active navigation item
  const getActiveValue = () => {
    // Special handling for data-import sub-routes
    if (location.pathname.startsWith('/data-import')) {
      return ROUTES.studyDataImporter;
    }

    // Special handling for file-upload sub-routes
    if (location.pathname.startsWith('/file-upload')) {
      return ROUTES.fileImporter;
    }

    // Default behavior - exact match
    return location.pathname;
  };

  // Get active item
  const activeValue = getActiveValue();
  const activeItem = availableItems.find(item => item.href === activeValue);

  // Determine if we should use compact mode based on available space
  // Use compact mode if we have more items than can fit in available space
  const shouldUseCompactMode = availableItems.length > itemsThatFit;

  // For loading state, show all items disabled
  if (!isMounted || isLoading) {
    return (
      <div ref={navRef} className="navigation">
        <SegmentedControl
          items={navItems.map(item => ({
            value: item.href,
            label: item.label,
          }))}
          value={getActiveValue()}
          onValueChange={() => {}}
          disabled
          fullWidth={false}
        />
      </div>
    );
  }

  // Compact mode: show SegmentedControl with active item (if exists) + dropdown for all items
  if (shouldUseCompactMode) {
    return (
      <div ref={navRef} className="navigation navigation--compact">
        <div className="navigation__content">
          {/* SegmentedControl with active item (only if active page is in navigation) */}
          {activeItem && (
            <div className="navigation__segmented-wrapper">
              <SegmentedControl
                items={[
                  {
                    value: activeItem.href,
                    label: (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        {activeItem.icon && <activeItem.icon />}
                        <span>{activeItem.label}</span>
                      </div>
                    ),
                  },
                ]}
                value={activeValue}
                onValueChange={() => {}} // Active item, no action needed
                fullWidth={false}
              />
            </div>
          )}

          {/* Dropdown for all navigation items */}
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Button
                variant="soft"
                className="navigation__dropdown-trigger rt-SegmentedControlItem"
              >
                <IconMenu />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              variant="soft"
              size="1"
              style={{ minWidth: 210 }}
            >
              {availableItems.map(item => (
                <DropdownMenuItem
                  key={item.href}
                  asChild
                  className={clsx(
                    'navigation__dropdown-item',
                    item.href === activeValue
                      ? 'navigation__dropdown-item--active'
                      : ''
                  )}
                >
                  <ListItem
                    as={Link}
                    startIcon={item.icon && <item.icon />}
                    primary={item.label}
                    endIcon={
                      item.href === activeValue && (
                        <IconCheck className="navigation__dropdown-item-check" />
                      )
                    }
                    to={item.href}
                  ></ListItem>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  }

  // Normal mode: use SegmentedControl with links
  return (
    <div ref={navRef} className="navigation">
      <SegmentedControl
        items={segmentedItems.map(item => ({
          ...item,
          // Make items clickable links that work with Ctrl+click and right-click copy
          value: item.value,
          label: (
            <a
              href={item.value}
              style={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
                width: '100%',
                height: '100%',
              }}
              onClick={e => {
                // If Ctrl/Cmd is pressed, let browser handle it (open in new tab)
                if (e.ctrlKey || e.metaKey) {
                  return;
                }
                // Otherwise prevent default and use React Router navigation
                e.preventDefault();
                if (item.value !== getActiveValue()) {
                  navigate(item.value);
                }
              }}
            >
              {item.label}
            </a>
          ),
        }))}
        value={getActiveValue()}
        onValueChange={() => {}} // Disable programmatic change, use links instead
        fullWidth
      />
    </div>
  );
};

export default Navigation;
